import { useEffect, useId, useMemo, useState, type FormEvent } from "react";
import { Link, useParams } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import { isNotFound, isUnauthorized } from "../../api/errors";
import { useHost, useNodePlacements, useUpdateNode } from "../../api/queries";
import type { Placement, Project, Service } from "../../api/types";
import { ErrorState, LoadingState } from "../../components/AsyncStates";
import { Banner } from "../../components/Banner";
import { Breadcrumb } from "../../components/Breadcrumb";
import { Button } from "../../components/Button";
import { DetailPageSkeleton } from "../../components/Skeleton";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { EnvironmentBadge } from "../../components/EnvironmentBadge";
import { HostAgentStatusPill, HostStatePill, NodeTypeBadge, PlacementStatusPill } from "../../components/StatePill";
import { useToast } from "../../toast/ToastContext";
import { ChangeEnvironmentModal } from "./ChangeEnvironmentModal";
import { useEnvironmentsById } from "../environments/useEnvironmentsById";
import { MachineContainersSection } from "./MachineContainersSection";
import { useServiceProjectIndex } from "../services/useServiceProjectIndex";
import { DEFAULT_GATEWAY_PORT } from "../services/useConnectionTopology";
import { providerLabel } from "./hostFormatting";
import { ProvisioningProgress } from "./ProvisioningProgress";
import "../../components/Form.css";
import "./host-detail.css";

interface ResolvedPlacement {
  placement: Placement;
  service: Service | undefined;
  project: Project | undefined;
}

const PLACEMENT_COLUMNS: DataTableColumn<ResolvedPlacement>[] = [
  {
    key: "service",
    header: "Service",
    cell: (r) =>
      r.project && r.service ? (
        <Link to={`/projects/${r.project.id}/services/${r.service.id}`}>{r.service.name}</Link>
      ) : (
        <span className="data-table__secondary">{r.placement.serviceId}</span>
      ),
    className: "data-table__primary",
  },
  {
    key: "project",
    header: "Project",
    cell: (r) => (r.project ? <Link to={`/projects/${r.project.id}`}>{r.project.name}</Link> : "—"),
  },
  { key: "status", header: "Status", cell: (r) => <PlacementStatusPill status={r.placement.status} /> },
];

/** Loosely matches `host` or `host:port` (an IP or DNS name, optionally with
 * a port) — the same human-facing shape `validateConnectAddress`
 * (server/internal/nodesvc/nodesvc.go) checks, kept intentionally permissive
 * (this is a first, friendly gate; the server remains the real one — S-8).
 * Empty is always valid (clears the override). */
function connectAddressLooksValid(raw: string): boolean {
  const addr = raw.trim();
  if (addr === "") return true;
  if (addr.length > 253) return false;
  if (/[\x00-\x1f\x7f ]/.test(addr)) return false;
  const lastColon = addr.lastIndexOf(":");
  if (lastColon !== -1 && addr.indexOf(":") === lastColon) {
    const host = addr.slice(0, lastColon);
    const port = Number(addr.slice(lastColon + 1));
    if (host === "") return false;
    if (!Number.isInteger(port) || port < 1 || port > 65535) return false;
  }
  return true;
}

/**
 * The unified Machine detail (IA slice 2, docs/product/ui-information-
 * architecture.md §3 — Fleet absorbs hosts∪nodes into ONE view): identity +
 * state up top, then the Environment card (with the "Change environment"
 * action that was previously a DevTools-only fix, the origin story of the
 * IA rework) and the Machine facts card side by side, then this machine's
 * containers with lifecycle verbs, then whatever Comebye has placed here.
 * Either the provisioning or live-agent facet section is entirely absent
 * (not a row of blank fields) when its facet is `null` — see `Host`'s doc
 * comment in types.ts.
 */
export function HostDetailPage() {
  const { hostId } = useParams<{ hostId: string }>();
  const { session } = useAuth();
  const { pushToast } = useToast();
  const { data: host, isPending, isError, error, refetch } = useHost(hostId);
  // Resolves the host's environment to its policy CLASS for the badge below
  // (Host/Deploy UX Phase 4, #526) — mirrors HostsListPage's Env column, now
  // styled consistently rather than as plain text.
  const environmentsById = useEnvironmentsById();

  // node:update (Change environment, connect-address edit) is admin-only AND
  // session-only server-side (#605) — hidden, not greyed (S-8: this is UX
  // only, the server enforces the real decision on every request).
  const isAdmin = session?.role === "admin";
  // container:update (the lifecycle verbs on the containers table below) is
  // granted to operator AND admin (server/internal/authz/model.go) — a
  // viewer sees the table read-only.
  const canManageContainers = canManageFleet(session?.role);

  const [changeEnvOpen, setChangeEnvOpen] = useState(false);

  const updateNodeMutation = useUpdateNode();
  const connectAddressFieldId = useId();
  const [connectAddress, setConnectAddress] = useState("");

  // Seeds the connect-address form from the loaded host and re-seeds
  // whenever the host *id* changes — mirrors ServiceDetailPage's identical
  // guard so an operator's in-progress edit is never silently clobbered by a
  // background refetch.
  useEffect(() => {
    if (!host?.liveAgent) return;
    setConnectAddress(host.liveAgent.connectAddress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host?.id]);

  function handleConnectAddressSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!hostId) return;
    updateNodeMutation.mutate(
      { nodeId: hostId, input: { connectAddress: connectAddress.trim() } },
      {
        onSuccess: () => pushToast({ title: "Connect address saved.", tone: "success" }),
        onError: (err) =>
          pushToast({
            title: "Couldn't save connect address",
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          }),
      },
    );
  }

  // Placements are keyed by node id, and a Host's `id` IS its node id
  // whenever it has an enrolled agent (see mockClient.ts's `computeHosts` /
  // the server's HostJSON join) — there is nothing to query while the host
  // has no live-agent facet yet (still provisioning, or provisioning failed).
  const placementsNodeId = host?.liveAgent ? host.id : undefined;
  const {
    data: placementsPage,
    isPending: placementsPending,
    isError: placementsIsError,
    error: placementsError,
    refetch: refetchPlacements,
  } = useNodePlacements(placementsNodeId);
  const { index: serviceIndex, isPending: indexPending, isError: indexIsError, error: indexError } =
    useServiceProjectIndex();

  const placements = useMemo(() => placementsPage?.items ?? [], [placementsPage]);
  const resolved: ResolvedPlacement[] = placements.map((placement) => {
    const hit = serviceIndex.get(placement.serviceId);
    return { placement, service: hit?.service, project: hit?.project };
  });

  if (isPending) {
    return <DetailPageSkeleton />;
  }

  if (isError) {
    if (isUnauthorized(error)) return <DetailPageSkeleton />;
    if (isNotFound(error)) {
      return (
        <ErrorState
          title="Machine not found"
          description="It may have been removed, or the link is out of date. Head back to the machines list."
          action={
            <Link to="/hosts" className="button button--secondary">
              Back to Machines
            </Link>
          }
        />
      );
    }
    return (
      <ErrorState
        description={error instanceof Error ? error.message : "Couldn't load this machine."}
        onRetry={refetch}
      />
    );
  }

  if (!host) {
    return (
      <ErrorState
        title="Machine not found"
        description="It may have been removed, or the link is out of date. Head back to the machines list."
        action={
          <Link to="/hosts" className="button button--secondary">
            Back to Machines
          </Link>
        }
      />
    );
  }

  const currentEnv = host.environmentId ? environmentsById.get(host.environmentId) : undefined;

  return (
    <div className="host-detail">
      <Breadcrumb items={[{ label: "Fleet", to: "/hosts" }, { label: host.name }]} />

      <div className="host-detail__header">
        <div className="host-detail__title-row">
          <h1 className="host-detail__title">{host.name}</h1>
          <HostStatePill state={host.state} />
          <NodeTypeBadge nodeType={host.nodeType} />
          {host.liveAgent && <span className="host-detail__id data-table__mono">{host.id}</span>}
        </div>
      </div>

      {/* Environment + Machine facts cards — side by side (IA slice 2 §3):
          "a machine's detail page carries its environment binding with a
          Change environment action." Both require a live-agent facet: an
          instance-only host (still provisioning) has no node identity to
          re-scope or report facts for yet. */}
      {host.liveAgent && (
        <div className="host-detail__cards-row">
          <section className="host-detail__card" aria-labelledby="host-environment-heading">
            <div className="host-detail__card-head">
              <h2 id="host-environment-heading">Environment</h2>
              {isAdmin && (
                <Button type="button" variant="secondary" onClick={() => setChangeEnvOpen(true)}>
                  Change environment…
                </Button>
              )}
            </div>

            {host.environmentName ? (
              <>
                <p className="host-detail__env-name">
                  <EnvironmentBadge name={host.environmentName} envClass={currentEnv?.class} />
                </p>
                {currentEnv && (
                  <p className="host-detail__env-facts">
                    {currentEnv.protected ? "Protected" : "Not protected"} ·{" "}
                    {currentEnv.requiresPromotion ? "promotion required" : "promotion not required"}
                  </p>
                )}
              </>
            ) : (
              <p className="host-detail__note">Not attached to an environment.</p>
            )}

            <p className="host-detail__env-caution">
              Moving a machine re-scopes everything on it. Local machines can never move into production.
            </p>
          </section>

          <section className="host-detail__card" aria-labelledby="host-facts-heading">
            <h2 id="host-facts-heading">Machine</h2>
            <dl className="host-detail__facts-grid">
              <div>
                <dt>Status</dt>
                <dd>
                  <HostAgentStatusPill status={host.liveAgent.status} />
                </dd>
              </div>
              <div>
                <dt>Agent version</dt>
                {/* Missing version = the heartbeat metadata hasn't arrived yet (in-memory
                    store, empty right after a CP restart) — an enrolled, connected agent
                    must never read "not enrolled" (A-10). */}
                <dd className={host.liveAgent.agentVersion ? "data-table__mono" : "data-table__empty-cell"}>
                  {host.liveAgent.agentVersion ?? "awaiting heartbeat"}
                </dd>
              </div>
              <div>
                <dt>Containers</dt>
                {/* Unknown until the first heartbeat — a hard 0 would be a lying number. */}
                <dd>{host.liveAgent.agentVersion ? host.liveAgent.containerCount : "—"}</dd>
              </div>
              <div>
                <dt>Last seen</dt>
                <dd>{host.liveAgent.lastSeenAt ? new Date(host.liveAgent.lastSeenAt).toLocaleString() : "never"}</dd>
              </div>
              <div>
                <dt>Type</dt>
                <dd>{host.nodeType || "unknown"}</dd>
              </div>
              <div>
                <dt>Hostname</dt>
                <dd className={host.hostname ? "data-table__mono" : "data-table__empty-cell"}>
                  {host.hostname || "not yet known"}
                </dd>
              </div>
            </dl>
          </section>
        </div>
      )}

      {/* Provisioning facet — entirely absent for a BYO host (`provisioning: null`),
          never an empty placeholder section (A-10). */}
      {host.provisioning && (
        <section className="host-detail__section" aria-labelledby="host-provisioning-heading">
          <h2 id="host-provisioning-heading">Provisioning</h2>

          {host.state === "provisioning" || host.state === "enrolling" ? (
            // "enrolling" only ever occurs with no node yet (the server's
            // running≠enrolled window — see mockClient.ts's computeHostState),
            // so there is no live-agent section for this host at all; the
            // progress framing belongs here, not there. ProvisioningProgress
            // renders the requested->provisioning->enrolling->online sequence
            // as a labeled step row, not just a spinner + sentence.
            <ProvisioningProgress host={host} variant="detail" />
          ) : host.state === "provision_failed" ? (
            <Banner
              tone="danger"
              title="Provisioning failed"
              action={
                <Link href="/instances" className="banner__link">
                  Review at Instances (advanced)
                </Link>
              }
            >
              Comebye couldn't finish provisioning this host at the provider. Destroy the failed request and
              provision again — it won't retry on its own.
            </Banner>
          ) : (
            <dl className="host-detail__meta">
              <div>
                <dt>Provider</dt>
                <dd>{providerLabel(host.provisioning.provider)}</dd>
              </div>
              <div>
                <dt>Region</dt>
                <dd className="data-table__mono">{host.provisioning.region}</dd>
              </div>
              <div>
                <dt>Server type</dt>
                <dd className="data-table__mono">{host.provisioning.serverType}</dd>
              </div>
              <div>
                <dt>Public IPv4</dt>
                <dd className={host.provisioning.publicIpv4 ? "data-table__mono" : "data-table__empty-cell"}>
                  {host.provisioning.publicIpv4 || "not yet assigned"}
                </dd>
              </div>
            </dl>
          )}
        </section>
      )}

      {/* Containers on this machine (IA slice 2 §8): per-container
          restart/stop/start for the Platform Lead's surgical cases —
          service-level actions live on Operate instead. */}
      {host.liveAgent && (
        <section className="host-detail__section" aria-labelledby="host-containers-heading">
          <div className="host-detail__card-head">
            <h2 id="host-containers-heading">Containers on this machine</h2>
            <span className="host-detail__note">
              Service-level actions live on each service in <Link to="/operate">Operate</Link>.
            </span>
          </div>
          <MachineContainersSection nodeId={host.id} canManage={canManageContainers} />
        </section>
      )}

      {/* Connect address (ADR-0029 §5): how OTHER machines reach this one
          directly — folded in here rather than a separate advanced page now
          that /nodes/:id no longer exists as its own route (IA slice 2). */}
      {host.liveAgent && (
        <section className="host-detail__section" aria-labelledby="host-connect-address-heading">
          <h2 id="host-connect-address-heading">How other machines reach this one</h2>
          <p className="form-field__hint">
            For cross-machine connections (e.g. one service needs to talk to another across hosts). Leave blank to
            use the address Comebye already sees this machine from.
          </p>
          {isAdmin ? (
            <form onSubmit={handleConnectAddressSubmit} noValidate>
              {updateNodeMutation.isError && (
                <p className="form-error" role="alert">
                  {updateNodeMutation.error instanceof Error
                    ? updateNodeMutation.error.message
                    : "Couldn't save the connect address."}
                </p>
              )}
              <label className="form-field" htmlFor={connectAddressFieldId}>
                <span>Connect address</span>
                <input
                  id={connectAddressFieldId}
                  type="text"
                  placeholder="host:port or IP (blank = use tunnel-observed address)"
                  value={connectAddress}
                  onChange={(e) => setConnectAddress(e.target.value)}
                />
                {!connectAddressLooksValid(connectAddress) && (
                  <span className="form-field__error">Enter a host or host:port — e.g. 10.0.0.5 or 10.0.0.5:4433.</span>
                )}
              </label>
              <p className="form-field__hint">
                Whichever address is in effect, peers still need TCP {DEFAULT_GATEWAY_PORT} (the gateway port, by
                default) reachable on it.
              </p>
              <div className="form-actions">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!connectAddressLooksValid(connectAddress) || updateNodeMutation.isPending}
                >
                  {updateNodeMutation.isPending ? "Saving…" : "Save"}
                </Button>
              </div>
            </form>
          ) : (
            <dl className="host-detail__meta">
              <div>
                <dt>Connect address</dt>
                <dd className={host.liveAgent.connectAddress ? "data-table__mono" : "data-table__empty-cell"}>
                  {host.liveAgent.connectAddress || "using tunnel-observed address"}
                </dd>
              </div>
            </dl>
          )}
        </section>
      )}

      <section className="host-detail__section" aria-labelledby="host-placements-heading">
        <h2 id="host-placements-heading">Placed services</h2>

        {!placementsNodeId ? (
          <p className="host-detail__note">
            Nothing can be placed here until this host has an enrolled agent.
          </p>
        ) : placementsPending ? (
          <LoadingState label="Loading placed services…" />
        ) : placementsIsError ? (
          <ErrorState
            description={
              placementsError instanceof Error
                ? placementsError.message
                : "Couldn't load placed services. Check your connection and try again."
            }
            onRetry={refetchPlacements}
          />
        ) : resolved.length === 0 ? (
          <p className="host-detail__note">Nothing placed on this host yet. Place services from their service page.</p>
        ) : (
          <>
            {indexIsError && (
              <p className="host-detail__index-warning" role="status">
                Couldn't resolve service/project names for every placement
                {indexError instanceof Error ? `: ${indexError.message}` : "."} Showing raw ids below.
              </p>
            )}
            <DataTable
              columns={PLACEMENT_COLUMNS}
              rows={resolved}
              rowKey={(r) => r.placement.id}
              ariaBusy={indexPending}
            />
          </>
        )}
      </section>

      {/* Platform/advanced view: raw provisioning records stay reachable —
          the pre-unification Node view was retired in IA slice 2 (its
          capabilities — environment, connect address, containers — are all
          on this page now). */}
      {host.provisioning && (
        <details className="host-detail__advanced">
          <summary>Advanced</summary>
          <ul className="host-detail__advanced-links">
            <li>
              <Link href="/instances">View provisioning records (advanced)</Link>
            </li>
          </ul>
        </details>
      )}

      {isAdmin && (
        <ChangeEnvironmentModal host={changeEnvOpen ? host : null} onClose={() => setChangeEnvOpen(false)} />
      )}
    </div>
  );
}
